import { ReactNode, ReactElement, cloneElement } from 'react';

import classNames from 'classnames';
import FormControlLabel from '@mui/material/FormControlLabel';

import { Box } from '../../Box';
import { Checkbox } from '../../Checkbox';
import { Label } from '../common';
import { TypographyColor } from '../../Typography';

import useStyles from './CheckboxInput.styles';

/**
 * Свойства компонента {@link CheckboxInput}
 */
interface CheckboxInputProps {
  /**
   * Состояние чекбокса.
   */
  checked?: boolean;

  /**
   * Заблокирован ли чекбокс.
   */
  disabled?: boolean;

  /**
   * Занимает ли чекбокс всю ширину.
   */
  fullWidth?: boolean;

  /**
   * Состояние неопределенности чекбокса.
   */
  indeterminate?: boolean;

  /**
   * Текст метки.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Позиция метки относительно чекбокса.
   */
  labelPlacement?: 'start' | 'end';

  /**
   * Цвет текста метки.
   */
  labelColor?: TypographyColor;

  /**
   * Вариант текста метки.
   */
  labelVariant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'body3'
    | 'caption'
    | 'button'
    | 'overline'
    | 'inherit';

  /**
   * Обработчик изменения состояния чекбокса.
   */
  onChange?: (value: boolean) => void;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode | null;

  /**
   * Подсказка для чекбокса.
   */
  checkboxTooltip?: ReactElement;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;
}

/**
 * CheckboxInput - компонент для отображения чекбокса с меткой.
 *
 * @public
 * @param checked - Состояние чекбокса.
 * @param disabled - Заблокирован ли чекбокс.
 * @param fullWidth - Занимает ли чекбокс всю ширину.
 * @param indeterminate - Состояние неопределенности чекбокса.
 * @param label - Текст метки.
 * @param labelIcon - Иконка для метки.
 * @param labelPlacement - Позиция метки относительно чекбокса.
 * @param labelColor - Цвет текста метки.
 * @param labelVariant - Вариант текста метки.
 * @param onChange - Обработчик изменения состояния чекбокса.
 * @param tooltip - Всплывающая подсказка.
 * @param checkboxTooltip - Подсказка для чекбокса.
 * @param testId - Идентификатор для тестирования.
 * @returns Возвращает JSX-элемент представляющий компонент CheckboxInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link CheckboxInputProps}
 */
export const CheckboxInput = ({
  checked,
  disabled = false,
  fullWidth = false,
  indeterminate,
  label,
  labelIcon,
  labelPlacement = 'end',
  labelColor = 'text.light.secondary',
  labelVariant = 'body3',
  onChange = () => {},
  tooltip,
  checkboxTooltip,
  testId,
  ...rest
}: CheckboxInputProps) => {
  const { classes } = useStyles();

  const control =
    disabled && checkboxTooltip ? (
      cloneElement(
        checkboxTooltip,
        {},
        <Box>
          <Checkbox
            {...rest}
            checked={checked}
            disabled
            indeterminate={indeterminate}
            onChange={onChange}
            testId={testId}
          />
        </Box>,
      )
    ) : (
      <Checkbox
        {...rest}
        checked={checked}
        disabled={disabled}
        indeterminate={indeterminate}
        onChange={onChange}
        testId={testId}
      />
    );

  return (
    <FormControlLabel
      className={classNames(
        classes.checkboxInput,
        'aqa_checkbox_input',
        fullWidth && classes.fullWidth,
      )}
      control={control}
      label={
        <Box
          mr={labelPlacement === 'start' ? '8' : '0'}
          ml={labelPlacement === 'end' ? '8' : '0'}
          pb="4"
        >
          <Label
            row
            label={label}
            tooltip={tooltip}
            color={labelColor}
            icon={labelIcon}
            variant={labelVariant}
          />
        </Box>
      }
      labelPlacement={labelPlacement}
    />
  );
};
